import React, { useMemo } from "react";
import { Dialog, DialogTitle, DialogContent, Grid, DialogActions } from "@material-ui/core";
import { CloseIcon } from "@icarius-icons/index";
import DialogTitleDivider from "@icarius-common/dialogTitleDivider";
import ButtonDialogAction from "@icarius-common/buttonDialogAction";
import PaperDraggable from "@icarius-common/paperDraggable";
import DialogTransition from "@icarius-common/dialogTransition";
import { getLocalizedString } from "@icarius-localization/strings";
import ReactApexChart from "react-apexcharts";
import { useSelector } from "react-redux";
import { getTheme } from "@icarius-pages/login/selectors";
import { getAppColor } from "@icarius-app/selectors";
import Table from "@icarius-pages/performanceSummaryAndResults/components/graphics/table";

const GraphicDialog = (props) => {
    const { open, data, noteList, handleClose } = props;

    const { theme } = useSelector(getTheme);
    const color = useSelector(getAppColor);

    const chartData = useMemo(() => {
        if (!data || data.length === 0) return null;

        if (noteList && noteList.length > 0) {
            const counts = noteList.map((n) => Number(n.quantity) || 0);
            return { counts };
        }

        return null;
    }, [data, noteList]);

    const chartOptions = {
        chart: {
            type: "area",
            toolbar: { show: false },
            zoom: {
                enabled: false,
            },
            selection: {
                enabled: false,
            },
        },
        states: {
            active: {
                filter: {
                    type: "none",
                },
            },
        },
        grid: {
            padding: {
                left: 0,
                right: 0,
            },
        },
        series: [
            {
                name: getLocalizedString("collaborators"),
                type: "area",
                data: chartData ? chartData.counts : [],
            },
        ],
        xaxis: {
            categories:
                noteList && noteList.length > 0
                    ? noteList.map((note) => getLocalizedString("fromTo", { from: note.from, to: note.to }))
                    : chartData
                    ? chartData.binLabels
                    : [],
            title: {
                text: getLocalizedString("finalGrade"),
                style: {
                    color: "var(--mainText)",
                    fontWeight: 600,
                    fontSize: "14px",
                },
            },
            labels: {
                style: {
                    colors: "var(--mainText)",
                },
            },
        },
        yaxis: {
            min: 0,
            max: (max) => (max === 0 ? 10 : Math.ceil(max * 1.2)),
            title: {
                text: getLocalizedString("collaborators"),
                style: {
                    color: "var(--mainText)",
                    fontWeight: 600,
                    fontSize: "14px",
                },
            },
            labels: {
                formatter: (value) => value.toFixed(0),
                style: {
                    colors: "var(--mainText)",
                },
            },
        },
        dataLabels: {
            enabled: false,
            formatter: (value) => value,
            style: {
                colors: ["var(--mainText)"],
            },
        },
        legend: {
            show: false,
        },
        colors: [color],
        fill: {
            type: "gradient",
            gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.7,
                opacityTo: 0.3,
                stops: [0, 90, 100],
            },
        },
        stroke: {
            curve: "smooth",
            width: 2,
        },
        tooltip: {
            theme: theme === "light" ? "light" : "dark",
            x: {
                formatter: function(value, { dataPointIndex }) {
                    if (noteList && noteList.length > 0 && noteList[dataPointIndex]) {
                        const note = noteList[dataPointIndex];
                        return `${note.name} (${getLocalizedString("fromTo", { from: note.from, to: note.to })})`;
                    }
                    return value;
                },
            },
        },
    };

    const formattedNoteList = noteList?.map((note) => {
        return [note.name, note.quantity, note.from, note.to];
    });

    return (
        <Dialog
            open={open}
            TransitionComponent={DialogTransition}
            PaperComponent={PaperDraggable}
            maxWidth={"lg"}
            fullWidth={true}
        >
            <div className={"dialog-container"}>
                <DialogTitle style={{ cursor: "move" }} id="draggable-dialog-title">
                    {getLocalizedString("gaussBell")}
                    <DialogTitleDivider />
                </DialogTitle>
                <CloseIcon className={"dialog-close-icon icon"} onClick={handleClose} />
                <DialogContent>
                    {chartData ? (
                        <Grid container item xs={12}>
                            <Grid item xs={12}>
                                <div style={{ overflow: "hidden", minHeight: "420px" }}>
                                    <ReactApexChart
                                        options={chartOptions}
                                        series={chartOptions.series}
                                        type="area"
                                        height={400}
                                    />
                                </div>
                            </Grid>
                            <Grid item xs={12}>
                                <Table
                                    list={["Nota", "Cantidad de evaluados", "Nota desde", "Nota hasta"]}
                                    data={formattedNoteList}
                                />
                            </Grid>
                        </Grid>
                    ) : (
                        <div className="whiteText" style={{ padding: 20, textAlign: "center" }}>
                            {getLocalizedString("noData")}
                        </div>
                    )}
                </DialogContent>
                <DialogActions>
                    <ButtonDialogAction onClick={handleClose} text={getLocalizedString("close") || "Cerrar"} />
                </DialogActions>
            </div>
        </Dialog>
    );
};

export default GraphicDialog;
